import type { DataTableColumnDef } from 'components/tables/data-table';
import { FilterConfig, MainFilter } from 'components/tables/list-table/ListTableTypes';
import ListTableWithFilters from 'components/tables/list-table/ListTableWithFilters';
import { useMemo } from 'react';
import { useLazyGetInvoiceTotalsByAccountingTypeReportQuery } from 'store/api/reportingFinance';
import { InvoiceTotalsByAccountingTypeRow, InvoiceTotalsReportTotals } from 'types/reporting/finance';
import { ReportPage } from 'types/reporting/reportPage';
import { t } from 'utils/getTranslations';
import { periodFilter, periodQuickFilter, quickFiltersFrom, searchSelectors } from '../reportFilters';
import { useReportBaseCurrency } from '../reportCurrency';
import { ReportLayout } from '../ReportLayout';
import { toneLegendOf } from '../ReportToneLegend';
import { ReportRowLimitNote } from '../ReportRowLimitNote';
import { ReportEntry } from '../reportCatalog';
import { invoiceTotalsColumns } from './financeReportColumns';
import { invoiceDateBasisFilter } from './financeReportFilters';

type Row = InvoiceTotalsByAccountingTypeRow;
type Totals = InvoiceTotalsReportTotals;

const REPORT_KEY = 'invoice-totals-by-accounting-type';

function buildFilterConfig(): FilterConfig {
    const filters: MainFilter[] = [periodFilter(), invoiceDateBasisFilter()];
    return {
        hideFilterDividers: true,
        quickFilterChips: true,
        periodQuickFilter: periodQuickFilter({ defaultPeriod: 'current-year' }),
        quickFilters: quickFiltersFrom(filters, { dateBasis: 'reporting.filter.date-basis' }),
        searchBarSelectors: searchSelectors('accountingType'),
        filters,
    };
}

function buildColumns(rows: Row[] | undefined, currency: string | null): DataTableColumnDef<Row, Totals>[] {
    return [
        {
            id: 'accountingType',
            accessorKey: 'accountingType',
            header: t('reporting.column.accounting-type'),
            meta: { widthWeight: 1.6, minPx: 200 },
        },
        ...invoiceTotalsColumns<Row>(rows, currency),
    ];
}

export function InvoiceTotalsByAccountingTypeReport({ report }: { report: ReportEntry }) {
    const [fetchData, { data, isFetching, isError }] = useLazyGetInvoiceTotalsByAccountingTypeReportQuery();
    const filterConfig = useMemo(() => buildFilterConfig(), []);
    const currency = useReportBaseCurrency(data?.totals?.baseCurrency ?? data?.content[0]?.baseCurrency);
    const columns = useMemo(() => buildColumns(data?.content, currency), [data, currency]);
    return (
        <ReportLayout report={report} fillHeight toneLegend={toneLegendOf(columns)} baseCurrency={currency}>
            <ListTableWithFilters<Row, ReportPage<Row, Totals>>
                data={data}
                belowQuickFilters={<ReportRowLimitNote report={report} rowCount={data?.content.length} />}
                isLoading={isFetching}
                isError={isError}
                fetchData={fetchData}
                filterConfig={filterConfig}
                columns={columns}
                columnConfigId={`report-${REPORT_KEY}`}
                enableColumnPicker
                enableColumnReorder
                enableColumnResize
                pluralTextId="reporting.accounting-types"
                singularTextId="reporting.accounting-type"
                noEntriesId="reporting.no-rows"
                noEntriesBodyId="reporting.no-rows-body"
            />
        </ReportLayout>
    );
}
